import useLocalStorage from "utils/useLocalStorage";
import { Title } from "utils/title";
import { FoundQuote, Quotes } from "./Quotes";
import { Vehicles } from "./Vehicles";
import { Checkbox, NumericTextBox } from "@progress/kendo-react-inputs";
import VehicleAutoComplete from "views/AssetVehicles/VehicleAutoComplete";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchApi } from "services/api";
import { Button } from "@progress/kendo-react-buttons";
import { Badge, BadgeContainer } from "@progress/kendo-react-indicators";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { LoadOneReconnectPolicy } from "services/signalr/retryPolicy";
import { UpdateQuoteAssigned } from "views/Quote";
import CustomerInput from "components/CustomerInput";
import { IDNameViewModel } from "TypeGen/id-name-view-model";

// Persisted Data
export type VehicleEntry = {
  VehicleID: number;
  Selected: boolean;
}

const QuoteFinder = () => {
  const [vehicles, setVehicles] = useLocalStorage<VehicleEntry[]>("QuoteFinder-Vehicles", []);
  const [distance, setDistance] = useLocalStorage<number>("QuoteFinder-Distance", 100);
  const [assigned, setAssigned] = useLocalStorage<boolean>("QuoteFinder-Assigned", false);
  const [billToCustomer, setBillToCustomer] = useLocalStorage<IDNameViewModel>("QuoteFinder-BillToCustomer", { ID: 0, Name: "" });
  const [alreadyBid, setAlreadyBid] = useLocalStorage<boolean>("QuoteFinder-AlreadyBid", false);

  const selectedVehicleIds = useMemo(() => vehicles.filter(v => v.Selected).map(v => v.VehicleID), [vehicles]);
  const [quoteDataLoading, setQuoteDataLoading] = useState(false);
  const [quoteData, setQuoteData] = useState<FoundQuote[]>([]);
  const [newQuoteCount, setNewQuoteCount] = useState(0);

  const assignedRef = useRef(assigned);
  useEffect(() => {
    assignedRef.current = assigned;
  }, [assigned]);

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withAutomaticReconnect(new LoadOneReconnectPolicy())
      .withUrl("/hub/quote")
      .configureLogging(LogLevel.Warning)
      .build();

    connection.onreconnected(() => {
      // TODO:
    });

    connection.on('newQuote', () => {
      setNewQuoteCount(prev => prev + 1);
    });

    connection.on('quoteAssigned', (event: UpdateQuoteAssigned) => {
      if (assignedRef.current) {
        setQuoteData(prev => prev.map(q => q.QuoteID === event.QuoteID ? { ...q, Unassigned: false, CreatedByUserName: event.CreatedByUserName } : q));
      } else {
        setQuoteData(prev => prev.filter(q => q.QuoteID !== event.QuoteID));
      }
    });

    connection.start().catch((error) => console.error(error));

    return () => {
      connection.stop();
    }
  }, []);

  const fetchQuotes = useCallback(() => {
    setQuoteData([]);
    setQuoteDataLoading(true);
    const payload = {
      VehicleIDs: selectedVehicleIds,
      Distance: distance || 0,
      Assigned: assigned,
      AlreadyBid: alreadyBid,
      CustomerID: billToCustomer.ID || null
    }
    fetchApi("/api/Quote/FinderQuotes", payload, "POST")
      .then(data => {
        setQuoteData(data.Quotes);
        setNewQuoteCount(0);
      })
      .finally(() => setQuoteDataLoading(false));
  }, [selectedVehicleIds, distance, assigned, alreadyBid, billToCustomer.ID]);

  useEffect(() => {
    if (selectedVehicleIds.length === 0) {
      setQuoteData([]);
      setQuoteDataLoading(false);
    } else {
      fetchQuotes();
    }
  }, [selectedVehicleIds, distance, assigned, fetchQuotes]);

  const toggleSelected = (vehicleID: number) => {
    setVehicles(vehicles.map(v => v.VehicleID === vehicleID ? { ...v, Selected: !v.Selected } : v));
  }

  const toggleSelectedAll = (select: boolean) => {
    setVehicles(vehicles.map(v => ({ ...v, Selected: select })));
  }

  const addVehicle = (vehicleID: number) => {
    setVehicles(prev => [...prev, { VehicleID: vehicleID, Selected: true }]);
  }

  const removeVehicle = (vehicleID: number) => {
    setVehicles(prev => prev.filter(v => v.VehicleID !== vehicleID));
  }

  return <div>
    <Title string="Quote Finder" />
    <div className="form-row mt-2">
      <div className="col-md-2">
        <VehicleAutoComplete
          setFocus={vehicles.length === 0}
          onSelectedVehicle={(v) => v !== null && addVehicle(v.VehicleID)}
          placeholder="Add Vehicle"
        />
      </div>
    </div>
    <Vehicles
      vehicles={vehicles}
      addVehicle={addVehicle}
      removeVehicle={removeVehicle}
      toggleSelected={toggleSelected}
      toggleSelectedAll={toggleSelectedAll}
    />
    <div className="form-row mt-2">
      <div className="col-md-1 d-flex align-items-center">
        <Checkbox
          label="Assigned"
          checked={assigned}
          onChange={(e) => setAssigned(e.value)}
        />
      </div>
      <div className="col-md-1 d-flex align-items-center">
        <Checkbox
          label="Already Bid"
          checked={alreadyBid}
          onChange={(e) => setAlreadyBid(e.value)}
        />
      </div>
      <div className="col-md-2 offset-6">
        <div className="input-group">
          <CustomerInput
            onCreditHold={false}
            placeholder="Bill To Company"
            CustomerID={billToCustomer.ID}
            CustomerNumber={billToCustomer.Name}
            onChange={(e) => {
              console.log(e);
              setBillToCustomer({ ID: e.CustomerID, Name: e.CustomerNumber });
            }}
          />
        </div>
      </div>
      <div className="col-md-1">
        <NumericTextBox
          value={distance}
          onChange={(e) => setDistance(e.value)}
          format="0 mi"
          className="flex-fill"
          min={5}
          max={500}
          spinners={false}
          placeholder="Distance"
        />
      </div>
      <div className="col-md-1">
        <BadgeContainer style={{ width: "100%" }}>
          <Button
            themeColor="primary"
            style={{ width: "100%" }}
            onClick={fetchQuotes}
          >
            Search
          </Button>
          {newQuoteCount > 0 && <Badge themeColor="error">{newQuoteCount}</Badge>}
        </BadgeContainer>
      </div>
    </div>
    <Quotes
      vehicleIds={vehicles.filter(v => v.Selected).map(v => v.VehicleID)}
      quoteData={quoteData}
      quoteDataLoading={quoteDataLoading}
    />
  </div>
};

export default QuoteFinder;